/* There actually isn't a v4 favourites endpoint yet, so we use v3. */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQuery } from "@nens/react-query-utils";

import { geometrySchema, lineStringSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import {
  OldFavouriteState,
  oldV1StateSchema,
  v1StateSchema,
  v2StateSchema,
} from "./legacy_favourites";

export const v3StateSchema = z.object({
  VERSION: z.literal(3),
  workspace: z.array(
    z.discriminatedUnion("type", [
      z.object({
        type: z.literal("lizardRaster"),
        uuid: z.string(),
        colorRange: z.tuple([z.number(), z.number()]).nullable(),
        selected: z.boolean(),
        visible: z.boolean(),
        opacity: z.number(),
      }),
      z.object({
        type: z.literal("lizardWmsLayer"),
        uuid: z.string(),
        selected: z.boolean(),
        visible: z.boolean(),
        opacity: z.number(),
      }),
      z.object({
        type: z.literal("scenario"),
        uuid: z.string(),
        selected: z.boolean(),
        rasterItems: z.array(
          z.object({
            type: z.literal("lizardRaster"),
            uuid: z.string(),
            colorRange: z.tuple([z.number(), z.number()]).nullable(),
            selected: z.boolean(),
            visible: z.boolean(),
            opacity: z.number(),
          })
        ),
      }),
      z.object({
        type: z.literal("monitoringNetwork"),
        uuid: z.string(),
        selected: z.boolean(),
        assetItems: z.array(
          z.object({
            type: z.literal("lizardIndividualAsset"),
            assetType: z.string(),
            assetId: z.number(),
            selected: z.boolean(),
            visible: z.boolean(),
          })
        ),
      }),
      z.object({
        type: z.literal("lizardIndividualAsset"),
        assetType: z.string(),
        assetId: z.number(),
        selected: z.boolean(),
        visible: z.boolean(),
      }),
      z.object({
        type: z.literal("lizardAssetSet"),
        assetType: z.string(),
        organisation: z.string(),
        selected: z.boolean(),
        visible: z.boolean(),
      }),
      z.object({
        type: z.literal("geometry"),
        geometry: geometrySchema,
        selected: z.boolean(),
        visible: z.boolean(),
        title: z.string().optional(),
      }),
    ])
  ),
  charts: z.object({
    charts: z.array(
      z.object({
        title: z.string(),
        data: z.array(
          z.object({
            parameter: z.string(),
            data: z.discriminatedUnion("type", [
              z.object({ type: z.literal("timeseries"), timeseriesUuid: z.string() }),
              z.object({
                type: z.literal("raster-asset"),
                rasterUuid: z.string(),
                asset: z.object({ assetType: z.string(), id: z.number() }),
              }),
              z.object({
                type: z.literal("raster-geometry"),
                rasterUuid: z.string(),
                geometry: geometrySchema,
              }),
              z.object({
                type: z.literal("raster-line"),
                rasterUuid: z.string(),
                geometry: lineStringSchema,
              }),
            ]),
          })
        ),
      })
    ),
    colors: z.array(
      z.tuple([
        z.discriminatedUnion("type", [
          z.object({ type: z.literal("timeseries"), timeseriesUuid: z.string() }),
          z.object({
            type: z.literal("raster-asset"),
            rasterUuid: z.string(),
            asset: z.object({ assetType: z.string(), id: z.number() }),
          }),
          z.object({
            type: z.literal("raster-geometry"),
            rasterUuid: z.string(),
            geometry: geometrySchema,
          }),
          z.object({
            type: z.literal("raster-line"),
            rasterUuid: z.string(),
            geometry: lineStringSchema,
          }),
        ]),
        z.string(),
      ])
    ),
  }),
  map: z.object({
    viewState: z.object({
      longitude: z.number(),
      latitude: z.number(),
      zoom: z.number(),
      pitch: z.number(),
      bearing: z.number(),
    }),
    bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  }),
  time: z.object({
    start: z.number(),
    end: z.number(),
    at: z.number(),
    now: z.number(),
    isRelative: z.boolean(),
    relativePeriod: z.tuple([z.number(), z.number()]),
    showTimePeriod: z.boolean(),
  }),
  ui: z.object({
    backgroundMap: z.string(),
    timeControlSize: z.string(),
    route: z.string(),
    customBackgroundMaps: z
      .array(
        z.object({
          internalUuid: z.string(),
          title: z.string(),
          url: z.string(),
          selected: z.boolean(),
        })
      )
      .optional(),
  }),
});

export type V3Favourite = z.infer<typeof v3StateSchema>;

export const favouriteStateSchema = z.discriminatedUnion("VERSION", [
  oldV1StateSchema,
  v1StateSchema,
  v2StateSchema,
  v3StateSchema,
]);

export const favouriteListItemSchema = z.object({
  uuid: z.string(),
  url: z.string(),
  name: z.string(),
});
export type FavouriteListItem = z.infer<typeof favouriteListItemSchema>;

export const favouriteListSchema = paginatedSchema(favouriteListItemSchema);
export type FavouriteList = z.infer<typeof favouriteListSchema>;

export const favouriteDetailSchema = favouriteListItemSchema.extend({
  state: favouriteStateSchema,
});
export type Favourite = z.infer<typeof favouriteDetailSchema>;
export type FavouriteState = Favourite["state"];

/* Hooks */

export function useFavourites() {
  return useParsedQuery<FavouriteList>(
    ["lizard-api-v4", "favourites"],
    () => fetchWithError("/api/v4/favourites/?page_size=100"),
    (json: unknown) => paginatedSchema(favouriteListItemSchema).parse(json)
  );
}

export function useFavourite(uuid: string) {
  return useParsedQuery<Favourite>(
    ["lizard-api-v4", "favourites", uuid],
    () => fetchWithError(`/api/v4/favourites/${uuid}/`),
    (json: unknown) => favouriteDetailSchema.parse(json)
  );
}

export function useDeleteFavourite() {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: deleteFavourite,
    onError: console.error,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lizard-api-v4", "favourites"] }),
  });

  return {
    deleteFavourite: mutate,
  };
}

export async function fetchFavourite(uuid: string): Promise<Favourite | null> {
  /* In the Redux thunk, we can't use React Query. Use this instead. */
  try {
    const json = await fetchWithError(`/api/v4/favourites/${uuid}/`);
    const favourite = favouriteDetailSchema.parse(json);
    return favourite;
  } catch (e) {
    return null;
  }
}

export function deleteFavourite(uuid: string) {
  return fetch(`/api/v4/favourites/${uuid}/`, {
    method: "DELETE",
  });
}

export async function saveFavourite(name: string, state: V3Favourite) {
  return fetchWithError(`/api/v4/favourites/`, {
    method: "POST",
    body: JSON.stringify({
      name,
      state,
    }),
  });
}

export function isOldFavourite(state: FavouriteState): state is OldFavouriteState {
  return !("VERSION" in state) || state.VERSION === 1 || state.VERSION === 2;
}
